import React, { useRef, useState } from 'react'
import { ChevronUp, ChevronDown, CheckCircle2, XCircle, Loader2 } from 'lucide-react'
import BlocklyWorkspace, { type BlocklyWorkspaceHandle } from './BlocklyWorkspace'

interface TestCase {
  id: string
  description: string
  expected_output: string
}

interface TestResult {
  id: string
  description: string
  expected_output: string
  actualOutput: string
  passed: boolean
}

interface ActivityData {
  question?: string
  test_cases?: TestCase[]
  hints?: string[]
  allowedCategories?: string[]
}

interface BlocklyActivityProps {
  activity: { id?: string; activity_data: ActivityData; title?: string; [key: string]: any }
  onSubmit: (responseData: Record<string, any>) => void
  onComplete?: () => void | Promise<void>
  disabled?: boolean
  hideSubmit?: boolean
}

function createSandboxSrc(code: string): string {
  const html = `<!DOCTYPE html><html><body><script>
    const _output = [];
    const _origLog = console.log;
    console.log = (...args) => { _output.push(args.map(String).join(' ')); _origLog(...args); };
    let _error = null;
    try {
      ${code}
    } catch(e) { _error = e.message; }
    parent.postMessage({ output: _output.join('\\n'), error: _error }, '*');
  <\/script></body></html>`
  return URL.createObjectURL(new Blob([html], { type: 'text/html' }))
}

export function BlocklyActivity({ activity, onSubmit, onComplete, disabled = false, hideSubmit = false }: BlocklyActivityProps) {
  const workspaceRef = useRef<BlocklyWorkspaceHandle>(null)
  const [output, setOutput] = useState<string | null>(null)
  const [execError, setExecError] = useState<string | null>(null)
  const [testResults, setTestResults] = useState<TestResult[] | null>(null)
  const [isRunning, setIsRunning] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [hintsOpen, setHintsOpen] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const blobUrlRef = useRef<string | null>(null)
  const startTimeRef = useRef<number>(Date.now())

  const data = activity.activity_data || {}
  const testCases: TestCase[] = data.test_cases || []
  const hints: string[] = data.hints || []
  const allowedCategories: string[] = data.allowedCategories || ['Logic', 'Loops', 'Math', 'Text', 'Variables', 'Functions']

  if (!hideSubmit && (submitted || disabled)) {
    // Show results with correct answers after submission (self-paced)
    if (submitted && onComplete && testResults && testResults.length > 0) {
      const passed = testResults.filter(r => r.passed).length
      const allPassed = passed === testResults.length
      return (
        <div className="p-2 sm:p-4 space-y-4">
          <div className={`flex items-center gap-3 px-4 py-3 rounded-xl ${allPassed ? 'bg-[var(--color-success-subtle)] border border-[var(--color-success)]/30' : 'bg-[var(--color-danger-subtle)] border border-[var(--color-danger)]/30'}`}>
            {allPassed
              ? <CheckCircle2 className="w-5 h-5 text-[var(--color-success)] shrink-0" />
              : <XCircle className="w-5 h-5 text-[var(--color-danger)] shrink-0" />
            }
            <span className={`font-semibold text-sm ${allPassed ? 'text-[var(--color-success)]' : 'text-[var(--color-danger)]'}`}>
              {allPassed ? 'All tests passed!' : `${passed}/${testResults.length} tests passed`}
            </span>
          </div>

          <div className="space-y-2">
            {testResults.map((tr) => (
              <div key={tr.id} className={`flex items-start gap-3 p-3 rounded-lg border ${
                tr.passed
                  ? 'bg-[var(--color-success-subtle)] border-[var(--color-success)]/30'
                  : 'bg-[var(--color-danger-subtle)] border-[var(--color-danger)]/30'
              }`}>
                {tr.passed
                  ? <CheckCircle2 className="w-5 h-5 text-[var(--color-success)] shrink-0 mt-0.5" />
                  : <XCircle className="w-5 h-5 text-[var(--color-danger)] shrink-0 mt-0.5" />
                }
                <div className="flex-1">
                  <p className="text-sm font-medium text-theme-text">{tr.description}</p>
                  <p className="text-xs text-theme-muted mt-1">
                    Expected: <code className="bg-[var(--color-surface-hover)] px-1 rounded">{tr.expected_output}</code>
                    {!tr.passed && (
                      <> — Got: <code className="bg-[var(--color-surface-hover)] px-1 rounded">{tr.actualOutput || '(empty)'}</code></>
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={() => onComplete()}
            className="w-full py-3 rounded-lg font-semibold text-sm transition-all hover:opacity-90 text-[var(--color-on-primary)]"
            style={{ background: 'var(--gradient-primary)' }}
          >
            See my results →
          </button>
        </div>
      )
    }

    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-subtle border border-theme-primary flex items-center justify-center">
          <svg className="w-8 h-8 text-theme-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-theme-primary mb-2">Waiting for Next Activity</h3>
        <p className="text-theme-text text-sm">
          {testResults && testResults.length > 0
            ? `${testResults.filter(r => r.passed).length}/${testResults.length} tests passed`
            : 'Your captain will start the next activity soon.'}
        </p>
      </div>
    )
  }

  const runCode = () => {
    if (!workspaceRef.current) return
    const code = workspaceRef.current.getGeneratedCode()

    setIsRunning(true)
    setOutput(null)
    setExecError(null)
    setTestResults(null)

    // Clean up previous blob URL and iframe
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current)
    }
    if (iframeRef.current) {
      iframeRef.current.remove()
    }

    const blobUrl = createSandboxSrc(code)
    blobUrlRef.current = blobUrl

    const iframe = document.createElement('iframe')
    iframe.style.display = 'none'
    iframe.sandbox.add('allow-scripts')
    document.body.appendChild(iframe)
    iframeRef.current = iframe

    const timeout = setTimeout(() => {
      setExecError('Execution timed out (5s)')
      setIsRunning(false)
      iframe.remove()
    }, 5000)

    const handler = (event: MessageEvent) => {
      if (event.source !== iframe.contentWindow) return
      clearTimeout(timeout)
      window.removeEventListener('message', handler)

      const { output: rawOutput, error } = event.data as { output: string; error: string | null }
      setOutput(rawOutput)
      setExecError(error)

      // Compare against test cases if any
      if (testCases.length > 0) {
        const results: TestResult[] = testCases.map((tc) => {
          const actual = (rawOutput || '').trim()
          const expected = tc.expected_output.trim()
          return {
            id: tc.id,
            description: tc.description,
            expected_output: expected,
            actualOutput: actual,
            passed: actual === expected,
          }
        })
        setTestResults(results)
      }

      setIsRunning(false)
      iframe.remove()
    }

    window.addEventListener('message', handler)
    iframe.src = blobUrl
  }

  const handleSubmit = async () => {
    if (!workspaceRef.current) return
    const code = workspaceRef.current.getGeneratedCode()
    const timeTaken = Math.floor((Date.now() - startTimeRef.current) / 1000)

    setIsSubmitting(true)
    await onSubmit({
      code,
      execution_result: testResults
        ? { test_results: testResults }
        : { output, error: execError },
      time_taken: timeTaken,
    })
    setSubmitted(true)
    setIsSubmitting(false)
  }

  return (
    <div className="space-y-5">
      {/* Question */}
      <div className="px-5 py-4">
        <p className="text-sm font-semibold text-green-500 leading-relaxed whitespace-pre-wrap">
          {data.question || 'No question provided.'}
        </p>
      </div>

      {/* Hints */}
      {hints.length > 0 && (
        <div className="mt-2 px-5">
          <button
            onClick={() => setHintsOpen((o) => !o)}
            className="flex items-center gap-1 bg-transparent border-none p-0 text-xs text-[var(--color-primary)] cursor-pointer"
          >
            {hintsOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {hintsOpen ? 'Hide hint' : 'Show hint'}
          </button>
          {hintsOpen && (
            <p className="mt-1 text-xs text-theme-text-muted italic bg-[var(--color-surface-hover)] rounded px-2 py-1">
              {hints[0]}
            </p>
          )}
        </div>
      )}

      {/* Blockly Workspace */}
      <div>
        <BlocklyWorkspace
          ref={workspaceRef}
          allowedCategories={allowedCategories}
          disabled={disabled || submitted}
        />
      </div>

      {/* Run button */}
      {!submitted && (
        <button
          onClick={runCode}
          disabled={isRunning || disabled}
          className="px-5 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors bg-theme-primary text-theme-on-primary"
        >
          {isRunning ? 'Running…' : 'Run Code'}
        </button>
      )}

      {/* Output panel */}
      {(output !== null || execError !== null) && (
        <div className="bg-theme-bg rounded-lg p-4 font-mono text-sm">
          <p className="text-theme-text-muted text-xs mb-2">Output</p>
          {execError && (
            <p className="text-[var(--color-danger)] mb-1">Error: {execError}</p>
          )}
          {output !== null && (
            <pre className="text-[var(--color-primary)] whitespace-pre-wrap">{output || '(no output)'}</pre>
          )}
        </div>
      )}

      {/* Test results */}
      {testResults && (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-theme-text">Test Results</p>
          {testResults.map((tr) => (
            <div
              key={tr.id}
              className={`flex items-start gap-3 p-3 rounded-lg border ${
                tr.passed
                  ? 'bg-[var(--color-success-subtle)] border-[var(--color-success)]/30'
                  : 'bg-[var(--color-danger-subtle)] border-[var(--color-danger)]/30'
              }`}
            >
              {tr.passed
                ? <CheckCircle2 className="w-5 h-5 text-[var(--color-success)] shrink-0 mt-0.5" />
                : <XCircle className="w-5 h-5 text-[var(--color-danger)] shrink-0 mt-0.5" />
              }
              <div className="flex-1">
                <p className="text-sm font-medium text-theme-text">{tr.description}</p>
                {!tr.passed && (
                  <p className="text-xs text-theme-muted mt-1">
                    Expected: <code className="bg-[var(--color-surface-hover)] px-1 rounded">{tr.expected_output}</code>{' '}
                    Got: <code className="bg-[var(--color-surface-hover)] px-1 rounded">{tr.actualOutput || '(empty)'}</code>
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Submit */}
      {!hideSubmit && !disabled && (
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full py-3 rounded-lg font-semibold text-sm transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-[var(--color-on-primary)]"
          style={{ background: 'var(--gradient-primary)' }}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Submitting…
            </span>
          ) : 'Submit Solution'}
        </button>
      )}
    </div>
  )
}
